import { PrescricaoSituation, Response, TipoReceitaSncr } from '_metronic/helpers'

export type SncrSaldoTipo = {
  tipo: TipoReceitaSncr
  saldo: number
  blocosNoMes: number
  limiarAlerta: number
  /** Blocos: saldo no limiar ou abaixo. Notificação: idem, mas só depois de `jaRetirou`. */
  alerta: boolean
  /** A inscrição já retirou este tipo alguma vez (diz se o médico usa o tipo). */
  jaRetirou: boolean
  /** Bloco retirado na Anvisa e ainda sem registro aqui: outra retirada do tipo daria 422. */
  retiradaEmAndamento: boolean
}

/** `GET /sncr/{idEmpresa}/contexto` */
export type SncrContexto = {
  habilitado: boolean
  /** Vale para os blocos (RCE/RET); notificação de receita exige número sempre. */
  exigirNumeracao: boolean
  inscricao?: string | null
  tamanhoBloco: number
  limiteBlocosMes: number
  /** Faixa aceita por pedido de Notificação de Receita (números avulsos). */
  quantidadeNotificacaoMinima: number
  quantidadeNotificacaoMaxima: number
  /** Login gov.br no mesmo ambiente da Anvisa que o backend usa; o front só acrescenta o `client_url`. */
  urlLogin?: string | null
  saldos: SncrSaldoTipo[]
}

export type SncrRetiradaDto = {
  token: string
  tipo: TipoReceitaSncr
  /** Só para notificação: quantos números avulsos retirar. RCE/RET vêm em bloco fixo. */
  quantidade?: number
}

/**
 * `POST /sncr/{idEmpresa}/retirar`. Para notificação, `numeroInicio`/`numeroFim` são o primeiro e o
 * último número liberados, `quantidade` o total e `limiteBlocosMes` vem zerado (o limite dela é diário).
 */
export type SncrRetiradaResultado = {
  tipo: TipoReceitaSncr
  inscricao: string
  numeroInicio: string
  numeroFim: string
  quantidade: number
  mensagem?: string | null
  saldo: number
  blocosNoMes: number
  limiteBlocosMes: number
  /** A gravação direta falhou; o backend conclui pelo stream em até um minuto e só então há saldo. */
  registroPendente: boolean
  /** Quanto ainda dá para retirar desse tipo hoje, segundo a Anvisa; nulo para RCE/RET. */
  saldoDiaAnvisa?: number | null
}

const TIPOS_NOTIFICACAO: ReadonlySet<TipoReceitaSncr> = new Set([
  TipoReceitaSncr.NRA,
  TipoReceitaSncr.NRB,
  TipoReceitaSncr.NRB2,
  TipoReceitaSncr.NRR,
])

/** Notificação de Receita: números avulsos, sem bloco mensal, consumidos pelo documento de notificação. */
export const isNotificacaoSncr = (tipo: TipoReceitaSncr) => TIPOS_NOTIFICACAO.has(tipo)

/**
 * Sem número desse tipo a receita não finaliza? Notificação eletrônica só existe numerada pelo SNCR
 * (RDC 1.000/2025), então exige sempre; os blocos seguem a flag de transição do backend.
 */
export const exigeNumeracaoSncr = (contexto: SncrContexto, tipo: TipoReceitaSncr) =>
  isNotificacaoSncr(tipo) || contexto.exigirNumeracao

export type SncrBloco = {
  id: number
  tipo: TipoReceitaSncr
  numeroInicio: string
  numeroFim: string
  quantidade: number
  saldo: number
  dataRetirada: string
  mensagem?: string | null
}

export type SncrBlocosQueryResponse = Response<SncrBloco[]>

/**
 * Prescrição vista pelo número SNCR (`GET /sncr/{idEmpresa}/blocos/{idBloco}/numeros` e
 * `GET /sncr/{idEmpresa}/numeros?numero=`). Cancelada continua na lista: o número não volta.
 */
export type SncrNumeroUsado = {
  id: number
  numeroSncr: string
  tipoReceitaSncr?: TipoReceitaSncr | null
  data?: string | null
  dataFormatada?: string | null
  situacao?: PrescricaoSituation | null
  atendimentoId?: number | null
  atendimentoProtocolo?: string | null
  pacienteId?: number | null
  pacienteNome?: string | null
}

export type SncrNumerosQueryResponse = Response<SncrNumeroUsado[]>

export const ROTULO_SITUACAO_PRESCRICAO: Record<PrescricaoSituation, string> = {
  [PrescricaoSituation.Rascunho]: 'Rascunho',
  [PrescricaoSituation.Gerada]: 'Gerada',
  [PrescricaoSituation.Assinada]: 'Assinada',
  [PrescricaoSituation.Finalizada]: 'Finalizada',
  [PrescricaoSituation.Cancelada]: 'Cancelada',
}

/** Números já consumidos do bloco: o ponteiro andou (quantidade − saldo), cancelados inclusive. */
export const numerosUsadosDoBloco = (bloco: Pick<SncrBloco, 'quantidade' | 'saldo'>) =>
  Math.max(0, bloco.quantidade - bloco.saldo)

export type SncrBlocosFiltros = {
  empresaId?: number
}

/** Códigos de `SncrNegocioException` do backend (422, `{data: {message, codigo}}`). */
export const SNCR_CODIGO = {
  SALDO_INSUFICIENTE: 'SALDO_INSUFICIENTE',
  TOKEN_INVALIDO: 'TOKEN_INVALIDO',
  TOKEN_EXPIRADO: 'TOKEN_EXPIRADO',
  VINCULO_INVALIDO: 'VINCULO_INVALIDO',
  TIPO_NAO_SUPORTADO: 'TIPO_NAO_SUPORTADO',
  LIMITE_BLOCOS_MES: 'LIMITE_BLOCOS_MES',
  INTEGRACAO_DESATIVADA: 'INTEGRACAO_DESATIVADA',
  RETIRADA_EM_ANDAMENTO: 'RETIRADA_EM_ANDAMENTO',
  SEM_NUMERACAO_DISPONIVEL: 'SEM_NUMERACAO_DISPONIVEL',
} as const

export const ROTULO_TIPO_RECEITA_SNCR: Record<TipoReceitaSncr, string> = {
  [TipoReceitaSncr.RCE]: 'Receita de Controle Especial',
  [TipoReceitaSncr.RET]: 'Receita com Retenção',
  [TipoReceitaSncr.NRA]: 'Notificação de Receita A',
  [TipoReceitaSncr.NRB]: 'Notificação de Receita B',
  [TipoReceitaSncr.NRB2]: 'Notificação de Receita B2',
  [TipoReceitaSncr.NRR]: 'Notificação de Receita de Retinoides',
}

export const saldoDoTipo = (contexto: SncrContexto | undefined, tipo: TipoReceitaSncr) =>
  contexto?.saldos.find((s) => s.tipo === tipo)

/** Como o número aparece em qualquer tela: `RCE 2610.6-32.0004001`; `undefined` sem número. */
export const rotuloNumeroSncr = (numero?: string | null, tipo?: TipoReceitaSncr | null) => {
  const limpo = numero?.trim()
  if (!limpo) return undefined
  return tipo ? `${tipo} ${limpo}` : limpo
}

/**
 * Inscrição que numera a receita, no formato do backend (`CRM 20018/ES`), a partir do vínculo
 * que já vem no login (`crm` chega como "número/UF"). `undefined` quando incompleta.
 */
export const inscricaoDoVinculo = (vinculo: {
  crm?: string | null
  conselho?: { sigla?: string | null } | null
}): string | undefined => {
  const sigla = vinculo.conselho?.sigla?.trim()
  const crm = vinculo.crm?.trim()
  return sigla && crm ? `${sigla.toUpperCase()} ${crm}` : undefined
}

/**
 * Empresa cuja inscrição (CRM/UF do vínculo) numera a receita: a do atendimento, nunca a do
 * login — o médico pode estar logado numa empresa e atendendo por outra.
 */
export const idEmpresaSncrDe = (atendimento?: {
  empresa?: { id?: number | string | null } | null
}): number | undefined =>
  atendimento?.empresa?.id != null ? Number(atendimento.empresa.id) : undefined
